import AppletError from '../../model/error';
import { SessionManager, UserSession } from './user-session';

export default class UserSessionCookie implements SessionManager {
  private authEndpoint = '';

  private initialized = false;

  init(): Promise<void> {
    // For cookie-based sessions, we don't need complex initialization
    // Just verify we're in a browser environment
    if (typeof document === 'undefined') {
      return Promise.reject(
        new AppletError(
          'browser_required',
          'Cookie-based session manager requires browser environment',
        ),
      );
    }

    // Set the auth endpoint to use the referrer domain since cookies are stored on the host domain
    const referrer = new URL(document.referrer);
    this.authEndpoint = `${referrer.origin}/ra/auth`;

    this.initialized = true;

    return Promise.resolve();
  }

  async getSession(appId: string): Promise<UserSession | null> {
    if (!this.initialized) {
      throw new Error('Call init() first');
    }

    try {
      // Get token from cookie via the auth endpoint
      const response = await fetch(this.authEndpoint, {
        method: 'GET',
        credentials: 'include', // Include cookies in the request
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        // No auth cookie or invalid
        return null;
      }

      if (!response.ok) {
        throw new AppletError(
          'session_retrieval_failed',
          `Failed to retrieve session: ${response.statusText}`,
          {
            code: response.status,
            status: response.status,
            request_id: '',
            message: `Failed to retrieve session: ${response.statusText}`,
          },
        );
      }

      const data = (await response.json()) as { token?: string };
      const { token } = data;

      if (!token) {
        return null;
      }

      return {
        appId,
        refreshToken: token,
      };
    } catch (error) {
      if (error instanceof AppletError) {
        throw error;
      }

      throw new AppletError(
        'session_retrieval_error',
        `Error retrieving session: ${error instanceof Error ? error.message : 'Unknown error'}`,
        {
          code: 500,
          status: 500,
          request_id: '',
          message: `Error retrieving session: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        },
      );
    }
  }

  async setSession(session: UserSession): Promise<void> {
    if (!this.initialized) {
      throw new Error('Call init() first');
    }

    try {
      const response = await fetch(this.authEndpoint, {
        method: 'POST',
        credentials: 'include', // Include cookies in the request
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: session.refreshToken ?? '',
        }),
      });

      if (!response.ok) {
        throw new AppletError(
          'session_set_failed',
          `Failed to set session: ${response.statusText}`,
          {
            code: response.status,
            status: response.status,
            request_id: '',
            message: `Failed to set session: ${response.statusText}`,
          },
        );
      }

      const data = (await response.json()) as { stored?: boolean; removed?: boolean };
      if ((session.refreshToken && !data.stored) || (!session.refreshToken && !data.removed)) {
        throw new AppletError('session_not_stored', 'Session was not stored successfully', {
          code: 500,
          status: 500,
          request_id: '',
          message: 'Session was not stored successfully',
        });
      }
    } catch (error) {
      if (error instanceof AppletError) {
        throw error;
      }

      throw new AppletError(
        'session_set_error',
        `Error setting session: ${error instanceof Error ? error.message : 'Unknown error'}`,
        {
          code: 500,
          status: 500,
          request_id: '',
          message: `Error setting session: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        },
      );
    }
  }
}
